import React, { useCallback, useEffect, memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, ListContainer } from "./createGigModal.styled";
import { Text } from "components/Typography/text.styled";
import { FontSize } from "config/font";
import { SimpleRow } from "../../organizationalStructure/organizationalStructure.styled";
import { Color } from "config/colors";
import { CustomIcon } from "components/icon/customIcon.component";
import {
  DropdownOption,
  DropdownWithLabel,
} from "../../general/dropDownWithLabel/dropdownWithLabel.component";
import { DropDownRow } from "../gigDetail/gigDetail.styled";
import { CompanyGig } from "model/Gig";
import { CompanyCustomer } from "model/Company";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";

export type Worker = {
  workerId?: string | number;
  id?: string | number;
  firstName: string;
  lastName: string;
  workGroups?: Array<{
    id?: string | number;
    workGroupId?: string | number;
    name?: string;
    workGroupName?: string;
  }>;
  employmentInfo?: {
    current?: {
      role?: string;
    };
  };
  selectedTeamId?: string;
  selectedCustomerProduct?: string;
  profit: number;
  profitSet: boolean;
};

type Props = {
  setWorkerListOpen: React.Dispatch<React.SetStateAction<boolean>>;
  workerListOpen: boolean;
  setSelectedWorkers: React.Dispatch<React.SetStateAction<Worker[]>>;
  selectedWorkers: Worker[];
  gigData?: CompanyGig;
};

const normalizeKey = (w: Worker) => String(w.workerId ?? w.id);

const teamValue = (team?: {
  id?: string | number;
  workGroupId?: string | number;
}) => (team ? String(team.id ?? team.workGroupId ?? "") : "");

const teamLabel = (team?: { name?: string; workGroupName?: string }) =>
  String(team?.name ?? team?.workGroupName ?? "");

const normalizeSelectedTeamId = (
  selectedTeamId?: string | number,
): string | undefined => {
  if (selectedTeamId == null) return undefined;
  const parsed = Number(selectedTeamId);
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
  return String(parsed);
};

export const WorkerList: React.FC<Props> = memo((props) => {
  const { t } = useTranslation();
  const [companyState, ,] = useCompanyStore();
  const [selectedCustomerForWorker, setSelectedCustomerForWorker] = useState<
    any[]
  >([]);
  const [selectedProductForWorker, setSelectedProductForWorker] = useState<
    any[]
  >([]);
  const [selectableProductsForWorker, setSelectableProductsForWorker] =
    useState<any[]>([]);
  const [customers, setCustomers] = useState<CompanyCustomer[]>([]);
  const [customersEnabled, setCustomersEnabled] = useState(false);

  useEffect(() => {
    let customersEnabledSetting = companyState.companySettings.find(
      (setting) => setting.key == "CustomersEnabled",
    );
    let customersIsEnabled =
      customersEnabledSetting && customersEnabledSetting.value == "true";
    setCustomersEnabled(!!customersIsEnabled);
  }, []);

  useEffect(() => {
    let companyId = companyState.company?.id;
    if (companyId) {
      Api()
        .company.getCompanyCustomers(companyId)
        .then((result) => {
          setCustomers(result.data);
        })
        .catch((err) => console.error(err));
    }
  }, []);

  const getProductsForCustomer = (customerId: any) => {
    let agreements = customers.find((c) => c.id === customerId)?.agreements;
    if (agreements && agreements.length > 0) {
      let products = agreements[0].products;
      return products;
    }
    return [];
  };

  useEffect(() => {
    if (!props.selectedWorkers?.length) return;

    let changed = false;
    const next = props.selectedWorkers.map((w) => {
      const groups = w.workGroups ?? [];
      const normalizedSelectedTeamId = normalizeSelectedTeamId(
        w.selectedTeamId,
      );
      if (groups.length === 1 && !normalizedSelectedTeamId) {
        changed = true;
        return { ...w, selectedTeamId: teamValue(groups[0]) };
      }
      if (w.selectedTeamId !== normalizedSelectedTeamId) {
        changed = true;
        return { ...w, selectedTeamId: normalizedSelectedTeamId };
      }
      return w;
    });

    if (changed) {
      props.setSelectedWorkers(next);
    }
  }, [props.selectedWorkers, props.setSelectedWorkers]);

  const handleToggleOpen = useCallback(() => {
    props.setWorkerListOpen((open) => !open);
  }, [props.setWorkerListOpen]);

  const handleRemove = useCallback(
    (workerKey: string) => {
      props.setSelectedWorkers((prev) =>
        prev.filter((w) => normalizeKey(w) !== workerKey),
      );
    },
    [props.setSelectedWorkers],
  );

  const handleTeamChange = useCallback(
    (workerKey: string, value: string) => {
      props.setSelectedWorkers((prev) =>
        prev.map((w) =>
          normalizeKey(w) === workerKey ? { ...w, selectedTeamId: value } : w,
        ),
      );
    },
    [props.setSelectedWorkers],
  );

  const handleProductChange = useCallback(
    (workerKey: string, value: string) => {
      props.setSelectedWorkers((prev) =>
        prev.map((w) =>
          normalizeKey(w) === workerKey
            ? { ...w, selectedCustomerProduct: value }
            : w,
        ),
      );
    },
    [props.setSelectedWorkers],
  );

  const handleCustomerSelectionForWorker = (worker: any, val: any) => {
    let products = getProductsForCustomer(val);
    let mappedProducts = products.map((product) => ({
      value: product.id,
      label: product.title,
    }));
    let selectable = { workerId: worker.workerId, products: mappedProducts };

    let productsForWorker = selectableProductsForWorker?.find(
      (f) => f.workerId === selectable.workerId,
    );
    if (productsForWorker) {
      productsForWorker.products = selectable.products;
      setSelectableProductsForWorker([...selectableProductsForWorker]);
    } else {
      let selectable = { workerId: worker.workerId, products: mappedProducts };
      setSelectableProductsForWorker([
        ...selectableProductsForWorker,
        selectable,
      ]);
    }

    let workerSelectedCustomer = selectedCustomerForWorker.find(
      (w) => w.workerId === worker.workerId,
    );

    if (workerSelectedCustomer) {
      workerSelectedCustomer.workerId = worker.workerId;
      workerSelectedCustomer.customer = customers.find((c) => c.id === val);
      setSelectedCustomerForWorker([...selectedCustomerForWorker]);
    } else {
      let selected = {
        workerId: worker.workerId,
        customer: customers.find((c) => c.id === val),
      };
      setSelectedCustomerForWorker([...selectedCustomerForWorker, selected]);
    }
  };

  const handleProductSelectionForWorker = (worker: any, productId: string) => {
    let customerId = selectedCustomerForWorker.find(
      (w) => w.workerId === worker.workerId,
    ).customer.id;
    let products = getProductsForCustomer(customerId);
    let workerProduct = products.find((p) => p.id === productId);
    let workerSalary = worker.employmentInfo.current.salary;

    worker.profitSet = true;
    worker.profit = (workerProduct?.price || 0) - workerSalary;

    let workerSelectedProduct = selectedProductForWorker.find(
      (w) => w.workerId === worker.workerId,
    );
    if (workerSelectedProduct) {
      workerSelectedProduct.workerId = worker.workerId;
      workerSelectedProduct.product = productId;
      setSelectableProductsForWorker([...selectableProductsForWorker]);
    } else {
      let selected = { workerId: worker.workerId, product: productId };
      setSelectedProductForWorker([...selectedProductForWorker, selected]);
    }
  };

  useEffect(() => {
    const structure: any = props.gigData?.structure;
    const gigWorkGroupName: string | undefined = structure?.workGroupName;

    if (!gigWorkGroupName) return;
    if (!props.selectedWorkers?.length) return;

    props.setSelectedWorkers((prev) => {
      let changed = false;

      const next = prev.map((w) => {
        const groups = w.workGroups ?? [];
        if (!groups.length) return w;

        const matchingGroup = groups.find(
          (g) => teamLabel(g) === gigWorkGroupName,
        );

        if (!matchingGroup) return w;

        const val = teamValue(matchingGroup);

        if (w.selectedTeamId === val) {
          return w;
        }

        changed = true;
        return { ...w, selectedTeamId: val };
      });

      return changed ? next : prev;
    });
  }, [props.gigData, props.selectedWorkers, props.setSelectedWorkers]);

  return (
    <>
      <DropDownRow onClick={handleToggleOpen}>
        <Text fontSize={FontSize.Standard}>
          {`${t("CreateShift.ChosenWorkers")} (${
            props.selectedWorkers?.length ?? 0
          })`}
        </Text>
        <CustomIcon
          name={props.workerListOpen ? "upward_arrow" : "downward_arrow"}
          size="24px"
          color={Color.BurntSienna}
          style={{ marginTop: 10 }}
        />
      </DropDownRow>

      {props.workerListOpen && props.selectedWorkers && (
        <ListContainer>
          {props.selectedWorkers.length > 0 &&
            props.selectedWorkers.map((worker) => {
              const workerKey = normalizeKey(worker);
              const groups = worker.workGroups ?? [];

              const options = groups
                .map((team) => ({
                  value: teamValue(team),
                  label: teamLabel(team) || t("CreateShift.UnnamedTeam"),
                }))
                .filter((o) => o.value !== "");

              const normalizedSelectedTeamId = normalizeSelectedTeamId(
                worker.selectedTeamId,
              );
              const value =
                normalizedSelectedTeamId ??
                (groups.length === 1 ? teamValue(groups[0]) : "");

              const isLocked =
                groups.length === 1 || props.gigData !== undefined;

              return (
                <>
                  <SimpleRow
                    key={`worker-${workerKey}`}
                    style={{
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: `1px solid ${Color.SeaBlue200}`,
                      width: "95%",
                      height: 80,
                      paddingBottom: "30px",
                      paddingTop: "30px",
                    }}
                  >
                    <Column>
                      <Text
                        style={{ marginBottom: 0 }}
                      >{`${worker.firstName}  ${worker.lastName}`}</Text>

                      <Text fontSize={FontSize.Small} style={{ marginTop: 0 }}>
                        {worker.employmentInfo &&
                        worker.employmentInfo.current?.role !== null
                          ? worker.employmentInfo.current?.role
                          : "-"}
                      </Text>
                    </Column>

                    {customersEnabled && (
                      <>
                        <Column>
                          <DropdownWithLabel
                            label={t("CompanyCustomers.Customer")}
                            options={customers.map(
                              (c) =>
                                ({
                                  value: c.id,
                                  label: c.name,
                                }) as DropdownOption,
                            )}
                            value={
                              selectedCustomerForWorker.find(
                                (w) => w.workerId === worker.workerId,
                              )?.customer.id
                            }
                            onChange={(val: string) =>
                              handleCustomerSelectionForWorker(worker, val)
                            }
                            required
                            inputSize="small"
                            width={200}
                            marginTop={5}
                            marginBottom={5}
                          />
                        </Column>

                        <Column>
                          <DropdownWithLabel
                            label={t("CompanyCustomers.CustomerProduct")}
                            options={
                              selectableProductsForWorker.find(
                                (f) => f.workerId === worker.workerId,
                              )?.products || []
                            }
                            value={
                              selectedProductForWorker.find(
                                (w) => w.workerId === worker.workerId,
                              )?.product
                            }
                            onChange={(val: string) => {
                              handleProductSelectionForWorker(worker, val);
                              handleProductChange(workerKey, val);
                            }}
                            placeholder={t("CreateShift.ChooseTeam")}
                            required
                            inputSize="small"
                            width={200}
                            marginTop={5}
                            marginBottom={5}
                          />
                        </Column>
                      </>
                    )}

                    <SimpleRow style={{ alignItems: "center" }}>
                      <DropdownWithLabel
                        label={t("CreateShift.Team")}
                        options={options}
                        value={value}
                        onChange={(val: string) =>
                          handleTeamChange(workerKey, val)
                        }
                        placeholder={t("CreateShift.ChooseTeam")}
                        required
                        inputSize="small"
                        width={200}
                        marginTop={5}
                        marginBottom={5}
                        disabled={isLocked}
                      />

                      {props.gigData == undefined && (
                        <div style={{ marginTop: 10 }}>
                          <CustomIcon
                            name="trashcan"
                            size="40px"
                            color={Color.LighterDestructive}
                            padding="10px"
                            hover
                            onClick={() => handleRemove(workerKey)}
                          />
                        </div>
                      )}
                    </SimpleRow>
                  </SimpleRow>
                  {worker.profitSet && (
                    <SimpleRow style={{ marginTop: "-30px" }}></SimpleRow>
                  )}
                </>
              );
            })}
        </ListContainer>
      )}
    </>
  );
});
